import { JSONSchema7 } from 'json-schema';

// Environment Security Settings Schema (reused in multiple places)
const environmentSecuritySettingsSchema: JSONSchema7 = {
    type: 'object',
    title: 'Environment Security Settings',
    properties: {
        crossOriginResourcePolicy: {
            type: 'string',
            title: 'Cross Origin Policy',
            enum: ['same-origin', 'same-site', 'cross-origin'],
        },
        referrerPolicy: {
            type: 'string',
            title: 'Referrer Policy',
            enum: [
                'no-referrer',
                'no-referrer-when-downgrade',
                'same-origin',
                'origin',
                'strict-origin',
                'origin-when-cross-origin',
                'strict-origin-when-cross-origin',
                'unsafe-url',
            ],
        },
        csp: {
            type: 'object',
            title: 'Content Security Policy',
            properties: {
                defaultSrc: {
                    type: 'string',
                    title: 'Default Source',
                    description: `The default-src is the default policy for loading content such as JavaScript, Images, CSS, Fonts, AJAX requests, Frames, HTML5 Media.`,
                },
                scriptSrc: {
                    type: 'string',
                    title: 'Script Source',
                    description: `The script-src directive restricts the locations from which a script can be executed.`,
                },
                scriptSrcAttr: {
                    type: 'string',
                    title: 'Script Source Attribute',
                    description: `The script-src-attr directive restricts the locations from which a script can be executed.`,
                },
                styleSrc: {
                    type: 'string',
                    title: 'Style Source',
                    description: `The style-src directive restricts the locations from which a style sheet can be applied.`,
                },
                imgSrc: {
                    type: 'string',
                    title: 'Image Source',
                    description: `The img-src directive restricts the locations from which images can be loaded.`,
                },
                connectSrc: {
                    type: 'string',
                    title: 'Connect Source',
                    description: `The connect-src directive restricts the locations which can be loaded using script interfaces.`,
                },
                fontSrc: {
                    type: 'string',
                    title: 'Font Source',
                    description: `The font-src directive restricts the locations from which fonts can be loaded.`,
                },
                objectSrc: {
                    type: 'string',
                    title: 'Object Source',
                    description: `The object-src directive restricts the locations from which plugins can be embedded.`,
                },
                mediaSrc: {
                    type: 'string',
                    title: 'Media Source',
                    description: `The media-src directive restricts the locations from which media can be loaded.`,
                },
                frameSrc: {
                    type: 'string',
                    title: 'Frame Source',
                    description: `The frame-src directive restricts the locations from which the protected resource can embed frames.`,
                },
            },
        },
        hsts: {
            type: 'object',
            title: 'HTTP Strict Transport Security',
            properties: {
                enabled: {
                    type: 'boolean',
                    title: 'Enabled',
                },
                maxAge: {
                    type: 'number',
                    title: 'Max Age',
                },
                includeSubDomains: {
                    type: 'boolean',
                    title: 'Include Subdomains',
                },
                preload: {
                    type: 'boolean',
                    title: 'Preload',
                },
            },
        },
        cors: {
            type: 'object',
            title: 'Cross Origin Resource Sharing',
            properties: {
                allowedOrigins: {
                    type: 'array',
                    title: 'Allowed Origins',
                    items: {
                        type: 'string',
                    },
                },
                allowHeaders: {
                    type: 'string',
                    title: 'Allowed Headers',
                },
                methods: {
                    type: 'string',
                    title: 'Methods',
                },
            },
        },
    },
};

// Basic Info Schema
export const basicInfoSchema: JSONSchema7 = {
    type: 'object',
    title: 'Basic Information',
    properties: {
        clientId: {
            type: 'string',
            title: 'Client ID',
            readOnly: true,
        },
        siteId: {
            type: 'string',
            title: 'Site ID',
            readOnly: true,
        },
        environmentName: {
            type: 'string',
            title: 'Environment Name',
            readOnly: true,
        },
        hostnames: {
            type: 'array',
            title: 'Hostnames',
            description: 'Add hostnames for this site',
            items: {
                type: 'string',
            },
        },
        favicon: {
            type: 'string',
            title: 'Favicon URL',
            description: 'Path to the favicon file',
            default: 'assets/favicon.ico',
        },
    },
};

// Data Stores Schema
export const dataStoresSchema: JSONSchema7 = {
    type: 'object',
    title: 'Data Stores Configuration',
    properties: {
        dataStores: {
            type: 'array',
            title: 'Data Stores',
            description: 'Configure data storage locations and settings',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Data Store ID',
                        description: 'Unique identifier for this data store',
                    },
                    path: {
                        type: 'string',
                        title: 'Data Store Path',
                        description: 'File system path or storage location',
                    },
                    addLastModified: {
                        type: 'boolean',
                        title: 'Add Last Modified',
                        description: 'Include last modified timestamp in responses',
                    },
                    addETag: {
                        type: 'boolean',
                        title: 'Add ETag',
                        description: 'Include ETag header for caching',
                    },
                    cacheControl: {
                        type: 'string',
                        title: 'Cache Control',
                        description: 'Cache control header value (e.g., max-age=3600)',
                    },
                },
                required: ['id', 'path'],
            },
        },
    },
};

// Environment sub-schemas for sub-tabs
export const environmentBasicSchema: JSONSchema7 = {
    type: 'object',
    title: 'Basic Configuration',
    description: 'Basic environment settings including ID and path configuration',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure basic environment settings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        description: 'Unique identifier (e.g., dev, staging, prod, uat)',
                        pattern: '^[a-z0-9-_]+$',
                        minLength: 2,
                        maxLength: 20,
                    },
                    environmentPath: {
                        type: 'string',
                        title: 'Environment Path',
                        description: 'Base path for this environment (used in URLs and file paths)',
                        minLength: 1,
                    },
                },
                required: ['id', 'environmentPath'],
            },
            minItems: 1,
        },
    },
};

export const environmentDataSourcesSchema: JSONSchema7 = {
    type: 'object',
    title: 'Data Sources',
    description: 'Configure environment-specific data source mappings',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure data source mappings for each environment',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    dataSources: {
                        type: 'array',
                        title: 'Data Sources',
                        description:
                            'Environment-specific data source mappings. Map production data sources to environment-specific alternatives.',
                        items: {
                            type: 'object',
                            title: 'Data Source Mapping',
                            properties: {
                                key: {
                                    type: 'string',
                                    title: 'Production Data Source ID',
                                    description: 'The ID of the data source in production',
                                },
                                value: {
                                    type: 'string',
                                    title: 'Environment Data Source Path',
                                    description: 'The alternative path for this environment',
                                },
                            },
                            required: ['key', 'value'],
                        },
                    },
                },
                required: ['id'],
            },
        },
    },
};

export const environmentVirtualDirsSchema: JSONSchema7 = {
    type: 'object',
    title: 'Virtual Directories',
    description: 'Configure virtual directory mappings for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure virtual directory mappings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    virtualDirs: {
                        type: 'array',
                        title: 'Virtual Directories',
                        description:
                            'Virtual directory mappings for this environment. Map URL paths to different physical locations.',
                        items: {
                            type: 'object',
                            title: 'Virtual Directory Mapping',
                            properties: {
                                key: {
                                    type: 'string',
                                    title: 'Virtual Directory ID',
                                    description: 'The virtual path identifier',
                                },
                                value: {
                                    type: 'string',
                                    title: 'Physical Directory Path',
                                    description: 'The actual file system path (optional)',
                                },
                            },
                            required: ['key'],
                        },
                    },
                },
                required: ['id'],
            },
        },
    },
};

export const environmentAnalyticsSchema: JSONSchema7 = {
    type: 'object',
    title: 'Analytics',
    description: 'Configure analytics settings for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure analytics settings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    enableAnalytics: {
                        type: 'boolean',
                        title: 'Enable Analytics',
                        description: 'Enable analytics tracking for this environment',
                        default: false,
                    },
                },
                required: ['id'],
            },
        },
    },
};

// Security sub-schemas for environment security sub-tabs
export const environmentSecurityGeneralSchema: JSONSchema7 = {
    type: 'object',
    title: 'General Policies',
    description: 'Configure general security policies for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure general security policies',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    securitySettings: {
                        type: 'object',
                        title: 'General Security Policies',
                        properties: {
                            crossOriginResourcePolicy: {
                                type: 'string',
                                title: 'Cross Origin Policy',
                                enum: ['same-origin', 'same-site', 'cross-origin'],
                            },
                            referrerPolicy: {
                                type: 'string',
                                title: 'Referrer Policy',
                                enum: [
                                    'no-referrer',
                                    'no-referrer-when-downgrade',
                                    'same-origin',
                                    'origin',
                                    'strict-origin',
                                    'origin-when-cross-origin',
                                    'strict-origin-when-cross-origin',
                                    'unsafe-url',
                                ],
                            },
                        },
                    },
                },
                required: ['id'],
            },
        },
    },
};

export const environmentSecurityCSPSchema: JSONSchema7 = {
    type: 'object',
    title: 'Content Security Policy',
    description: 'Configure Content Security Policy settings for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure CSP settings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    securitySettings: {
                        type: 'object',
                        title: 'Content Security Policy',
                        properties: {
                            csp: {
                                type: 'object',
                                title: 'Content Security Policy',
                                properties: {
                                    defaultSrc: {
                                        type: 'string',
                                        title: 'Default Source',
                                        description: `The default-src is the default policy for loading content such as JavaScript, Images, CSS, Fonts, AJAX requests, Frames, HTML5 Media.`,
                                    },
                                    scriptSrc: {
                                        type: 'string',
                                        title: 'Script Source',
                                        description: `The script-src directive restricts the locations from which a script can be executed.`,
                                    },
                                    scriptSrcAttr: {
                                        type: 'string',
                                        title: 'Script Source Attribute',
                                        description: `The script-src-attr directive restricts the locations from which a script can be executed.`,
                                    },
                                    styleSrc: {
                                        type: 'string',
                                        title: 'Style Source',
                                        description: `The style-src directive restricts the locations from which a style sheet can be applied.`,
                                    },
                                    imgSrc: {
                                        type: 'string',
                                        title: 'Image Source',
                                        description: `The img-src directive restricts the locations from which images can be loaded.`,
                                    },
                                    connectSrc: {
                                        type: 'string',
                                        title: 'Connect Source',
                                        description: `The connect-src directive restricts the locations which can be loaded using script interfaces.`,
                                    },
                                    fontSrc: {
                                        type: 'string',
                                        title: 'Font Source',
                                        description: `The font-src directive restricts the locations from which fonts can be loaded.`,
                                    },
                                    objectSrc: {
                                        type: 'string',
                                        title: 'Object Source',
                                        description: `The object-src directive restricts the locations from which plugins can be embedded.`,
                                    },
                                    mediaSrc: {
                                        type: 'string',
                                        title: 'Media Source',
                                        description: `The media-src directive restricts the locations from which media can be loaded.`,
                                    },
                                    frameSrc: {
                                        type: 'string',
                                        title: 'Frame Source',
                                        description: `The frame-src directive restricts the locations from which the protected resource can embed frames.`,
                                    },
                                },
                            },
                        },
                    },
                },
                required: ['id'],
            },
        },
    },
};

export const environmentSecurityHSTSSchema: JSONSchema7 = {
    type: 'object',
    title: 'HTTP Strict Transport Security',
    description: 'Configure HSTS settings for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure HSTS settings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    securitySettings: {
                        type: 'object',
                        title: 'HSTS Settings',
                        properties: {
                            hsts: {
                                type: 'object',
                                title: 'HTTP Strict Transport Security',
                                properties: {
                                    enabled: {
                                        type: 'boolean',
                                        title: 'Enabled',
                                    },
                                    maxAge: {
                                        type: 'number',
                                        title: 'Max Age',
                                    },
                                    includeSubDomains: {
                                        type: 'boolean',
                                        title: 'Include Subdomains',
                                    },
                                    preload: {
                                        type: 'boolean',
                                        title: 'Preload',
                                    },
                                },
                            },
                        },
                    },
                },
                required: ['id'],
            },
        },
    },
};

export const environmentSecurityCORSSchema: JSONSchema7 = {
    type: 'object',
    title: 'Cross Origin Resource Sharing',
    description: 'Configure CORS settings for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure CORS settings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    securitySettings: {
                        type: 'object',
                        title: 'CORS Settings',
                        properties: {
                            cors: {
                                type: 'object',
                                title: 'Cross Origin Resource Sharing',
                                properties: {
                                    allowedOrigins: {
                                        type: 'array',
                                        title: 'Allowed Origins',
                                        items: {
                                            type: 'string',
                                        },
                                    },
                                    allowHeaders: {
                                        type: 'string',
                                        title: 'Allowed Headers',
                                    },
                                    methods: {
                                        type: 'string',
                                        title: 'Methods',
                                    },
                                },
                            },
                        },
                    },
                },
                required: ['id'],
            },
        },
    },
};

export const environmentSecuritySchema: JSONSchema7 = {
    type: 'object',
    title: 'Security Settings',
    description: 'Configure security settings for each environment',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description: 'Configure security settings',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        readOnly: true,
                    },
                    securitySettings: {
                        ...environmentSecuritySettingsSchema,
                        description:
                            'Security settings specific to this environment. These override global security settings.',
                    },
                },
                required: ['id'],
            },
        },
    },
};

// Environments Schema (legacy - keeping for backward compatibility)
export const environmentsSchema: JSONSchema7 = {
    type: 'object',
    title: 'Environment Configuration',
    description:
        'Manage different deployment environments (development, staging, production, etc.)',
    properties: {
        environments: {
            type: 'array',
            title: 'Environments',
            description:
                'Configure different deployment environments. Each environment can have its own data sources, security settings, and configurations.',
            items: {
                type: 'object',
                title: 'Environment',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Environment ID',
                        description: 'Unique identifier (e.g., dev, staging, prod, uat)',
                        pattern: '^[a-z0-9-_]+$',
                        minLength: 2,
                        maxLength: 20,
                    },
                    environmentPath: {
                        type: 'string',
                        title: 'Environment Path',
                        description: 'Base path for this environment (used in URLs and file paths)',
                        minLength: 1,
                    },
                    dataSources: {
                        type: 'array',
                        title: 'Data Sources',
                        description:
                            'Environment-specific data source mappings. Map production data sources to environment-specific alternatives.',
                        items: {
                            type: 'object',
                            title: 'Data Source Mapping',
                            properties: {
                                key: {
                                    type: 'string',
                                    title: 'Production Data Source ID',
                                    description: 'The ID of the data source in production',
                                },
                                value: {
                                    type: 'string',
                                    title: 'Environment Data Source Path',
                                    description: 'The alternative path for this environment',
                                },
                            },
                            required: ['key', 'value'],
                        },
                    },
                    virtualDirs: {
                        type: 'array',
                        title: 'Virtual Directories',
                        description:
                            'Virtual directory mappings for this environment. Map URL paths to different physical locations.',
                        items: {
                            type: 'object',
                            title: 'Virtual Directory Mapping',
                            properties: {
                                key: {
                                    type: 'string',
                                    title: 'Virtual Directory ID',
                                    description: 'The virtual path identifier',
                                },
                                value: {
                                    type: 'string',
                                    title: 'Physical Directory Path',
                                    description: 'The actual file system path (optional)',
                                },
                            },
                            required: ['key'],
                        },
                    },
                    enableAnalytics: {
                        type: 'boolean',
                        title: 'Enable Analytics',
                        description: 'Enable analytics tracking for this environment',
                        default: false,
                    },
                    securitySettings: {
                        ...environmentSecuritySettingsSchema,
                        description:
                            'Security settings specific to this environment. These override global security settings.',
                    },
                },
                required: ['id', 'environmentPath'],
            },
            minItems: 1,
        },
    },
};

// Analytics Schema
export const analyticsSchema: JSONSchema7 = {
    type: 'object',
    title: 'Analytics Configuration',
    properties: {
        analyticsSettings: {
            type: 'object',
            title: 'Analytics Settings',
            description: 'Configure analytics tracking and reporting',
            properties: {
                analyticsEngine: {
                    type: 'string',
                    title: 'Analytics Engine',
                    description: 'Select the analytics platform to use',
                    enum: ['none', 'google-analytics', 'adobe-analytics', 'mixpanel'],
                    default: 'none',
                },
                googleAnalyticsSettings: {
                    type: 'object',
                    title: 'Google Analytics Settings',
                    properties: {
                        analyticsTrackingId: {
                            type: 'string',
                            title: 'GA Tracking ID',
                            description: 'Google Analytics tracking ID (e.g., GA-XXXXXXXXX-X)',
                            pattern: '^(GA|G)-[A-Z0-9-]+$',
                        },
                    },
                },
                aemSettings: {
                    type: 'object',
                    title: 'Adobe Experience Manager Settings',
                    properties: {
                        aemLaunchUrl: {
                            type: 'string',
                            title: 'AEM Launch URL',
                            description: 'Adobe Experience Manager Launch script URL',
                            format: 'uri',
                        },
                    },
                },
            },
        },
    },
};

// Authorization Schema
export const authorizationSchema: JSONSchema7 = {
    type: 'object',
    title: 'Authorization Configuration',
    properties: {
        authorization: {
            type: 'object',
            title: 'Authorization Settings',
            description: 'Configure access control and security policies',
            properties: {
                enableIPWhitelisting: {
                    type: 'boolean',
                    title: 'Enable IP Whitelisting',
                    description: 'Restrict access to specific IP addresses',
                },
                ipWhitelist: {
                    type: 'array',
                    title: 'IP Whitelist',
                    description: 'List of allowed IP addresses or CIDR blocks',
                    items: {
                        type: 'string',
                        pattern:
                            '^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:/[0-9]{1,2})?$',
                    },
                },
            },
            dependencies: {
                enableIPWhitelisting: {
                    oneOf: [
                        {
                            properties: {
                                enableIPWhitelisting: { const: false },
                            },
                        },
                        {
                            properties: {
                                enableIPWhitelisting: { const: true },
                                ipWhitelist: {
                                    type: 'array',
                                    minItems: 1,
                                    items: { type: 'string' },
                                },
                            },
                            required: ['ipWhitelist'],
                        },
                    ],
                },
            },
        },
    },
};

// Global Security Schema
export const securitySchema: JSONSchema7 = {
    type: 'object',
    title: 'Global Security Configuration',
    properties: {
        securitySettings: {
            ...environmentSecuritySettingsSchema,
            title: 'Global Security Settings',
            description: 'Security settings that apply across all environments',
        },
    },
};
